/**
 * LeadSource iframe launch helpers.
 *
 * TEMP (Todd testing): GHL locationId → plan code + launch base URL.
 * FINAL: same prod launch URL for all; plan from email DB lookup.
 *
 * Plan codes: starter=aca4e89324, plus=bde4789352, pro=cacbd12238
 */

export const PLAN_CODES = {
  starter: "aca4e89324",
  plus: "bde4789352",
  pro: "cacbd12238",
};

/** Launch bases from Cameron WhatsApp (env tiers). */
export const LAUNCH_URLS = {
  // Beta 1 — existing / current default
  dev: "https://sdai-dev.theleadsource.com/launch",
  // Beta 2 — app
  app: "https://sdai.theleadsource.com/ghl-push-3/launch",
  // Beta 3 — stg
  stg: "https://sdai-stg.theleadsource.com/ghl-push-3/launch",
};

function normalizeLaunchBase(url) {
  return String(url || "")
    .trim()
    .replace(/\?.*$/, "")
    .replace(/\/+$/, "");
}

/** Profile query params stay on the DEV launch host until a signed launch context exists. */
export function isDevLaunchBase(baseUrl) {
  return normalizeLaunchBase(baseUrl) === normalizeLaunchBase(LAUNCH_URLS.dev);
}

/**
 * Append optional TLS profile params. Empty values are omitted so an existing
 * TLS profile value is left unchanged. No-op unless the launch base is DEV.
 */
export function appendDevProfileParams(params, baseUrl, profile = {}) {
  if (!isDevLaunchBase(baseUrl)) return params;

  const entries = [
    ["firstname", profile.firstName],
    ["lastname", profile.lastName],
    ["alternateemail", profile.alternateEmail],
    ["phone", profile.phone],
  ];

  for (const [key, raw] of entries) {
    let value = String(raw ?? "").trim();
    if (key === "alternateemail") value = value.toLowerCase();
    if (value) params.set(key, value);
  }

  return params;
}

/**
 * Temporary per-location overrides (Cameron WhatsApp).
 * Key = GHL location ID.
 */
export const TEMP_LOCATION_OVERRIDES = {
  // LeadSource Beta = Starter = dev (existing iframe URL)
  GEsLi1ZFqtLEn9eCOY96: {
    label: "[Test] LeadSource Beta",
    plan: PLAN_CODES.starter,
    launchUrl: LAUNCH_URLS.dev,
  },
  // LeadSource Beta 2 = Plus = app
  W8C1iNkoFTwYHX0MGNkU: {
    label: "[Test] LeadSource Beta 2",
    plan: PLAN_CODES.plus,
    launchUrl: LAUNCH_URLS.app,
  },
  // LeadSource Beta 3 = Pro = stg
  "7Pu9yBKB06u4NmOy2llh": {
    label: "[Test] LeadSource Beta 3",
    plan: PLAN_CODES.pro,
    launchUrl: LAUNCH_URLS.stg,
  },
};

function parseEnvOverrides() {
  const raw = process.env.REACT_APP_LEADSOURCE_LOCATION_OVERRIDES;
  if (!raw || !String(raw).trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    console.warn("[LeadSource] Invalid REACT_APP_LEADSOURCE_LOCATION_OVERRIDES JSON");
    return {};
  }
}

/** Merge file map + optional env JSON (env wins on same locationId). */
export function getLocationOverrides() {
  return { ...TEMP_LOCATION_OVERRIDES, ...parseEnvOverrides() };
}

/**
 * Resolve plan + launch base for a location.
 * Temp override wins; otherwise callers use email DB / defaults.
 */
export function resolveLocationLaunchOverride(locationId) {
  const id = (locationId || "").trim();
  if (!id) return null;
  const map = getLocationOverrides();
  const entry = map[id];
  if (!entry) return null;

  let plan = (entry.plan || "").trim();
  if (plan && PLAN_CODES[plan]) plan = PLAN_CODES[plan];

  const launchUrl = (entry.launchUrl || entry.iframeUrl || "").trim().replace(/\?$/, "");

  return {
    label: entry.label || id,
    plan: plan || null,
    launchUrl: launchUrl || null,
  };
}
